import { useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { Download, Trash2, Upload } from 'lucide-react';
import { clsx } from 'clsx';
import { Button, ConfirmDialog, Spinner } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { ImportBatch } from '../api/verificationApi';
import { formatTimestamp, pluralize } from '../utils/format';
import styles from './UploadStep.module.css';

interface UploadStepProps {
  onFile: (file: File) => void;
  onUseSample: () => void;
  onDownloadTemplate: () => void;
  parsing: boolean;
  downloading: boolean;
  batches: ImportBatch[];
  batchesLoading: boolean;
  /** Undefined when the current user can't delete imports (controls are hidden). */
  onDeleteBatch?: (batch: ImportBatch) => void;
  deletingBatchId?: string | null;
}

const ACCEPT = '.csv,.xlsx';

export function UploadStep({
  onFile,
  onUseSample,
  onDownloadTemplate,
  parsing,
  downloading,
  batches,
  batchesLoading,
  onDeleteBatch,
  deletingBatchId,
}: UploadStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ImportBatch | null>(null);
  const canDelete = !!onDeleteBatch;

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onFile(file);
    e.target.value = '';
  }

  return (
    <div className={styles.root}>
      <div
        role="button"
        tabIndex={0}
        className={clsx(styles.drop, dragging && styles.dropActive, parsing && styles.dropBusy)}
        onClick={() => !parsing && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !parsing) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragging) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className={styles.fileInput}
          onChange={handleChange}
          aria-label="Choose a schedule file"
        />
        <div className={styles.dropIcon}>
          {parsing ? <Spinner size="sm" /> : <Upload size={22} aria-hidden />}
        </div>
        <div className={styles.dropTitle}>
          {parsing ? 'Reading your file…' : 'Drop a CSV or Excel file, or click to choose'}
        </div>
        <div className={styles.dropSub}>
          Export tomorrow's schedule from your practice software and drop it here
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Download size={14} aria-hidden />}
          onClick={onDownloadTemplate}
          isLoading={downloading}
        >
          Download blank template
        </Button>
        <Button variant="secondary" size="sm" onClick={onUseSample} disabled={parsing}>
          Use sample file →
        </Button>
      </div>

      <div className={styles.note}>
        <strong>Required columns:</strong> patient name, date of birth, appointment type, carrier,
        and member ID. <strong>Optional:</strong> appointment date/time, provider, group number,
        and subscriber details when the patient is a dependent. Column names don't have to match —
        you'll map them next.
      </div>

      <section className={styles.recent}>
        <div className={patterns.sectionLabel}>Recent imports</div>
        <div className={patterns.gridTable}>
          <div className={clsx(patterns.gridTableHeader, canDelete ? styles.batchColsDeletable : styles.batchCols)}>
            <span>File</span>
            <span>Rows</span>
            <span>Created</span>
            <span>Updated</span>
            <span>Flagged</span>
            <span>Imported</span>
            {canDelete && <span aria-label="Actions" />}
          </div>
          {batchesLoading && batches.length === 0 && (
            <div className={patterns.gridTableEmpty}>
              <Spinner size="sm" />
            </div>
          )}
          {!batchesLoading && batches.length === 0 && (
            <div className={patterns.gridTableEmpty}>
              No imports yet — your first one will show up here.
            </div>
          )}
          {batches.map((b) => (
            <div key={b.id} className={clsx(patterns.gridTableRow, canDelete ? styles.batchColsDeletable : styles.batchCols)}>
              <div className={patterns.cellPrimary}>{b.fileName}</div>
              <div className={patterns.cellSecondary}>{b.rowCount}</div>
              <div className={patterns.cellSecondary}>{b.createdCount}</div>
              <div className={patterns.cellSecondary}>{b.updatedCount}</div>
              <div className={b.failedCount > 0 ? styles.flagged : patterns.cellSecondary}>
                {b.failedCount}
              </div>
              <div className={patterns.cellMuted}>{formatTimestamp(b.createdAt)}</div>
              {canDelete && (
                <button
                  type="button"
                  className={styles.trashBtn}
                  aria-label={`Delete import ${b.fileName}`}
                  title="Delete this import and its appointments"
                  onClick={() => setPendingDelete(b)}
                  disabled={deletingBatchId === b.id}
                >
                  {deletingBatchId === b.id ? <Spinner size="sm" /> : <Trash2 size={15} aria-hidden />}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete && onDeleteBatch) onDeleteBatch(pendingDelete);
          setPendingDelete(null);
        }}
        title="Delete this import?"
        message={
          pendingDelete ? (
            <>
              Removes <strong>{pendingDelete.fileName}</strong> from recent imports and deletes the{' '}
              {pluralize(pendingDelete.activeCount, 'appointment')} it loaded, with their audit trails.
              {pendingDelete.activeCount < pendingDelete.rowCount && (
                <> Rows re-imported by a later file stay with that import.</>
              )}{' '}
              Rows already written to Open Dental are kept. This can't be undone.
            </>
          ) : null
        }
        confirmLabel="Delete import"
        variant="destructive"
      />
    </div>
  );
}
